import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Home, Search, User, LogOut, Users, PlusCircle, Bell, MessageSquare, Bookmark } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { subscribeRealtime } from "../lib/realtime";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [notifCount, setNotifCount] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  const [hasChildren, setHasChildren] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/guardian/children")
      .then(({ data }) => setHasChildren(data.length > 0))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const poll = () => {
      api.get("/notifications/unread-count").then(({ data }) => setNotifCount(data.count)).catch(() => {});
      api.get("/messages/unread-count").then(({ data }) => setMsgCount(data.count)).catch(() => {});
    };
    poll();
    const t = setInterval(poll, 60000);
    const unsub = subscribeRealtime((event) => {
      if (event.type === "notification" || event.type === "message") poll();
    });
    return () => {
      clearInterval(t);
      unsub();
    };
  }, [user, loc.pathname]);

  const isActive = (p) => loc.pathname === p;

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/discover?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#262626]"
    >
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="font-display text-2xl tracking-widest text-white shrink-0 hover:opacity-80 transition-opacity"
        >
          EGNCY
        </Link>

        <form onSubmit={submitSearch} className="flex-1 max-w-md hidden sm:block">
          <div className="relative">
            <Search className="w-4 h-4 text-[#737373] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="navbar-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search players, teams, positions"
              className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-[#404040] outline-none pl-10 pr-4 py-2 rounded-full text-sm text-white placeholder:text-[#737373] transition-colors"
            />
          </div>
        </form>

        <nav className="ml-auto flex items-center gap-1">
          {user ? (
            <>
              <NavIcon to="/" active={isActive("/")} label="Home" testid="nav-home">
                <Home className="w-5 h-5" />
              </NavIcon>
              <NavIcon to="/discover" active={isActive("/discover")} label="Discover" testid="nav-discover">
                <Users className="w-5 h-5" />
              </NavIcon>
              <NavIcon to="/create" active={isActive("/create")} label="Post" testid="nav-create">
                <PlusCircle className="w-5 h-5" />
              </NavIcon>
              <NavIcon to="/messages" active={loc.pathname.startsWith("/messages")} label="Messages" testid="nav-messages" badge={msgCount}>
                <MessageSquare className="w-5 h-5" />
              </NavIcon>
              <NavIcon to="/notifications" active={isActive("/notifications")} label="Alerts" testid="nav-notifications" badge={notifCount}>
                <Bell className="w-5 h-5" />
              </NavIcon>
              {(user.role === "scout" || user.role === "coach") && (
                <NavIcon to="/shortlists" active={isActive("/shortlists")} label="Shortlists" testid="nav-shortlists">
                  <Bookmark className="w-5 h-5" />
                </NavIcon>
              )}
              {hasChildren && (
                <NavIcon to="/family" active={isActive("/family")} label="Family" testid="nav-family">
                  <Users className="w-5 h-5" />
                </NavIcon>
              )}
              <NavIcon to={`/profile/${user.id}`} active={loc.pathname.startsWith("/profile")} label="Profile" testid="nav-profile">
                <User className="w-5 h-5" />
              </NavIcon>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="ml-2 p-2 text-[#a3a3a3] hover:text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
                aria-label="Log out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="px-4 py-2 text-sm text-white hover:bg-[#1a1a1a] rounded-sm transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-link"
                className="px-4 py-2 text-sm bg-white text-black font-semibold rounded-sm hover:bg-gray-200 transition-colors"
              >
                Join EGNCY
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function NavIcon({ to, active, label, testid, badge = 0, children }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative flex flex-col items-center px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors rounded-sm ${
        active
          ? "text-white bg-[#1a1a1a]"
          : "text-[#a3a3a3] hover:text-white hover:bg-[#1a1a1a]"
      }`}
    >
      {children}
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute top-0 right-1 min-w-[16px] h-[16px] px-1 bg-white text-black text-[9px] font-bold rounded-full flex items-center justify-center"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      <span className="mt-0.5 hidden md:inline">{label}</span>
    </Link>
  );
}
